import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { SalesStatusBadge } from "@/components/sales/SalesStatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SALES_TABS } from "@/lib/salesConstants";
import { formatKr, formatDate, daysLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Plus, Receipt, Search, X } from "lucide-react";

export default function SalesInvoicesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const status = params.get("status") || "alle";
  const q = params.get("q") || "";
  const customerId = params.get("customer_id") || "";
  const [search, setSearch] = useState(q);

  useEffect(() => { setSearch(q); }, [q]);

  const load = () => {
    setData(null);
    api.get("/sales/invoices", { params: { status, q: q || undefined, customer_id: customerId || undefined, limit: 100 } }).then((r) => setData(r.data)).catch((e) => setError(errorMessage(e)));
  };
  useEffect(load, [status, q, customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  const submitSearch = (e) => { e.preventDefault(); setParam("q", search.trim()); };
  const counts = data?.counts || {};
  const hasFilters = !!(q || customerId);

  const emptyText = useMemo(() => {
    if (hasFilters) return { title: "Ingen treff", description: "Prøv et annet søk eller nullstill filteret." };
    if (status === "kladd") return { title: "Ingen kladder", description: "Kladder er fakturaer du ikke har utstedt ennå." };
    if (status === "forfalt") return { title: "Ingen forfalte fakturaer", description: "Alle kunder betaler i tide. Flott!" };
    if (status === "betalt") return { title: "Ingen betalte fakturaer ennå", description: "Registrer betaling på en faktura når pengene er kommet inn." };
    if (status === "kreditnota") return { title: "Ingen kreditnotaer", description: "Kreditnota lages fra en utstedt faktura." };
    if (status === "sanert") return { title: "Ingen sanerte dokumenter", description: "Når en faktura og en kreditnota med samme beløp saneres mot hverandre, havner begge her og forsvinner fra de daglige oversiktene." };
    if (status === "apen") return { title: "Ingen ubetalte fakturaer", description: "Alt er betalt." };
    return { title: "Ingen fakturaer ennå", description: "Lag din første faktura – det tar under et minutt." };
  }, [status, hasFilters]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fakturaer"
        description="Alle fakturaer og kreditnotaer du har laget."
        actions={<Button asChild data-testid="sales-invoices-new-button"><Link to="/faktura/fakturaer/ny"><Plus className="h-4 w-4 mr-2" /> Ny faktura</Link></Button>}
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={status} onValueChange={(v) => setParam("status", v === "alle" ? "" : v)} className="w-full lg:w-auto">
          <TabsList className="h-auto flex-wrap justify-start gap-1 bg-transparent p-0" data-testid="sales-status-tabs">
            {SALES_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="rounded-full border data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=active]:border-transparent data-[state=active]:shadow-none px-3 py-1.5 text-sm" data-testid={`sales-tab-${t.value}`}>
                {t.label}{counts[t.value] !== undefined ? <span className="ml-1.5 text-xs text-muted-foreground tnum">{counts[t.value]}</span> : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <form onSubmit={submitSearch} className="relative w-full lg:w-80" role="search">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søk kunde, nummer, referanse…" className="pl-9 pr-9" data-testid="sales-search-input" aria-label="Søk i fakturaer" />
          {search ? <button type="button" onClick={() => { setSearch(""); setParam("q", ""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Tøm søk" data-testid="sales-search-clear"><X className="h-4 w-4" /></button> : null}
        </form>
      </div>

      {customerId ? <div className="text-sm text-muted-foreground flex items-center gap-2">Filtrert på kunde <Button variant="outline" size="sm" className="h-7" onClick={() => setParam("customer_id", "")} data-testid="sales-clear-customer-filter"><X className="h-3.5 w-3.5 mr-1" /> Fjern</Button></div> : null}

      {error ? <EmptyState title="Kunne ikke laste fakturaer" description={error} actionLabel="Prøv igjen" onAction={load} /> : !data ? (
        <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : data.items.length === 0 ? (
        <EmptyState icon={Receipt} title={emptyText.title} description={emptyText.description} actionLabel={hasFilters ? "Nullstill filter" : status === "alle" ? "Ny faktura" : undefined} onAction={hasFilters ? () => setParams({}) : undefined} actionTo={!hasFilters && status === "alle" ? "/faktura/fakturaer/ny" : undefined} testId="sales-invoices-empty" />
      ) : (
        <>
          <div className="hidden md:block rounded-xl border bg-card overflow-hidden">
            <Table data-testid="sales-invoice-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[110px]">Nummer</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead className="w-[120px]">Dato</TableHead>
                  <TableHead className="w-[150px]">Forfall</TableHead>
                  <TableHead className="w-[130px]">Status</TableHead>
                  <TableHead className="text-right w-[140px]">Beløp</TableHead>
                  <TableHead className="text-right w-[140px]">Utestående</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((i) => (
                  <TableRow key={i.id} className="cursor-pointer" onClick={() => navigate(`/faktura/fakturaer/${i.id}`)} data-testid={`sales-invoice-row-${i.id}`}>
                    <TableCell className="font-medium tnum">{i.number_display || <span className="text-muted-foreground">Kladd</span>}</TableCell>
                    <TableCell><p className="font-medium truncate max-w-[280px]">{i.customer?.name || <span className="text-muted-foreground">Uten kunde</span>}</p>{i.status === "sanert" && i.settlement?.counterpart_number ? <p className="text-xs text-muted-foreground">Sanert mot {i.settlement.counterpart_type === "kreditnota" ? "kreditnota" : "faktura"} {i.settlement.counterpart_number}</p> : i.type === "kreditnota" ? <p className="text-xs text-muted-foreground">Kreditnota for {i.credits_number_display}</p> : i.your_reference ? <p className="text-xs text-muted-foreground truncate max-w-[280px]">Ref. {i.your_reference}</p> : null}</TableCell>
                    <TableCell className="tnum text-muted-foreground">{formatDate(i.issue_date)}</TableCell>
                    <TableCell className="tnum">{i.type === "kreditnota" ? <span className="text-muted-foreground">–</span> : <><span>{formatDate(i.due_date)}</span>{["utstedt", "sendt", "delvis_betalt", "forfalt"].includes(i.display_status) ? <p className={cn("text-xs", i.display_status === "forfalt" ? "text-flag" : "text-muted-foreground")}>{daysLabel(i.days_to_due)}</p> : null}</>}</TableCell>
                    <TableCell><SalesStatusBadge status={i.display_status} type={i.type} /></TableCell>
                    <TableCell className="text-right font-medium tnum whitespace-nowrap">{i.type === "kreditnota" ? `−${formatKr(i.total)}` : formatKr(i.total)}</TableCell>
                    <TableCell className="text-right tnum whitespace-nowrap text-muted-foreground">{i.type === "faktura" && i.outstanding > 0 ? <span className={cn("font-medium", i.display_status === "forfalt" ? "text-flag" : "text-foreground")}>{formatKr(i.outstanding)}</span> : "–"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="md:hidden space-y-2" data-testid="sales-invoice-cards">
            {data.items.map((i) => (
              <li key={i.id}>
                <Link to={`/faktura/fakturaer/${i.id}`} className="block rounded-xl border bg-card p-4 card-lift" data-testid={`sales-invoice-card-${i.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0"><p className="font-medium truncate">{i.customer?.name || "Uten kunde"}</p><p className="text-xs text-muted-foreground">{i.type === "kreditnota" ? "Kreditnota" : "Faktura"} {i.number_display || "(kladd)"} · {formatDate(i.issue_date)}</p></div>
                    <SalesStatusBadge status={i.display_status} type={i.type} />
                  </div>
                  <div className="mt-3 flex items-end justify-between">
                    <p className={cn("text-xs", i.display_status === "forfalt" ? "text-flag" : "text-muted-foreground")}>{i.type === "faktura" && i.due_date ? `Forfall ${formatDate(i.due_date)}` : ""}</p>
                    <p className="font-heading font-semibold tnum whitespace-nowrap">{i.type === "kreditnota" ? `−${formatKr(i.total)}` : formatKr(i.total)}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">{data.total} {data.total === 1 ? "dokument" : "dokumenter"}</p>
        </>
      )}
    </div>
  );
}
